import React, { FC, createContext, useState, useContext, useCallback, useEffect } from "react";

import { IPlayer, IGame } from "../interfaces";
import { SocketContext } from "./SocketContext";
import { mapTeamPlayers } from "./helpers";
import axios from "axios";
import mapLanguage from "../util/helpers/mapLanguage";

interface ITeamContext {
  teamNumber?: string;
  players: IPlayer[];
  game?: IGame;
  activateAction: boolean;
  isTheButtonClickedToJoinTheGame: boolean, 
  allTeamNames: any; 
  numberOfPlayers: number,
  selectedLanguage: string;
  enterTheTeam(teamNumber: string): void;
  joinLobby(payload: any): void;
  roomUsers(): void;
  setGameState(data: any): void;
  sendMessage(insertedMessage: any): void;
  setActivateAction(activateAction: boolean): void;
  setIsTheButtonClickedToJoinTheGame(isTheButtonClickedToJoinTheGame: boolean): void;
  setAllTeamNames(allTeamNames: any): void;
  urlToken: string | undefined; 
  setUrlToken(urlToken: string | undefined): void;
  gameId: string | undefined; 
  setGameId(gameId: string | undefined): void;
  isLoaderHidden: boolean;
  setisLoaderHidden(isLoaderHidden: boolean): void;
  gameCount: number;
  setGameCount(gameCount: number): void;
  reConnectToServer(): void;
  getGameLanguage(): void;
  userDataHelper: IPlayer | undefined;
  setSelectedLanguage(selectedLanguage: string): void;
}

const TeamContext = createContext({} as ITeamContext);

let isGamePhaseIsNine = false;
let markAction = 0;

interface TeamContextProps {
  children: React.ReactNode;
}

const TeamContextProvider: FC<TeamContextProps> = (props) => {
  const { socket } = useContext(SocketContext);
  const windowLocationHref = window.location.href;
  const urlPathChunks = windowLocationHref.split("/");
  const urlTokenExtracted = urlPathChunks[urlPathChunks.length-1];

  
  const [game, setGame] = useState<IGame>();
  const [teamNumber, setTeamNumber] = useState<string>(urlTokenExtracted);
  const [players, setPlayers] = useState<IPlayer[] | []>([]);
  const [activateAction, setActivateAction] = useState<boolean>(false);
  const [isTheButtonClickedToJoinTheGame, setIsTheButtonClickedToJoinTheGame] = useState<boolean>(false);
  const [allTeamNames, setAllTeamNames] = useState<any>();
  const [urlToken, setUrlToken] = useState<string | undefined>();
  const [gameId, setGameId] = useState<string | undefined>();
  const [isLoaderHidden, setisLoaderHidden] = useState<boolean>(true);
  const [gameCount, setGameCount] = useState<number>(0);
  const numberOfPlayers: number = game?.players.length || 0;
  const [userDataHelper, setUserDataHelper] = useState<IPlayer | undefined>();
  const [selectedLanguage, setSelectedLanguage] = useState<string>("en");

  useEffect(() => {
    if(game){
      const numberOfPlayers = game?.players.length || 0;
      setGameId(game.matchId)
      game?.players.forEach( (item: any, index: number) => {
        localStorage.setItem(`userEmail${index}`, item.email);
      })
      localStorage.setItem(`numberOfPlayers`, numberOfPlayers.toString());
    }
  }, [game])
  
  const sendMessage = (insertedMessage: any) => {
    socket?.emit("sendMessage", insertedMessage);
  };

  const joinLobby = (payload: any) => {
    socket?.emit("joinRoom", payload);
  };

  const enterTheTeam = (teamNumber: string) => {
    setTeamNumber(teamNumber);
  };

  const roomUsers = useCallback(() => {
    console.log("roomUsers method");
    
    socket?.on("roomUsers", (data: any) => {
      console.log("inside of the socket roomUsers");
      console.log("data: ", data);

      if (!data) return;
      
      let dataId = data.id;
      var indexOfSlashCharacter = dataId.indexOf("/");
      let teamNumberFromTheLink = dataId.slice(indexOfSlashCharacter+1, dataId.length);
      
      enterTheTeam(teamNumberFromTheLink);

      if(data?.phase===9){
        isGamePhaseIsNine=true;
      }

      if(data?.phase === 3 && markAction === 5){
        setActivateAction(true);
      }

      markAction = data?.phase;

      setGame(data);
      setPlayers(mapTeamPlayers(data?.players, data?.gameInfo?.players));
    });

    socket?.on("disconnect", () => {
      setGame(undefined);
      setPlayers([]);
    });
  }, [socket]);

  const getGameLanguage = () => {
    axios({
      method: "get",
      url: process.env.REACT_APP_ADMIN_BACKEND_URL + `api/preactive-matches/get-language/${urlTokenExtracted}`,
    })
    .then((res: any) => {
          const gameLanguage = res.data.language;
          setSelectedLanguage(mapLanguage(gameLanguage));
    })
    .catch((err: any) => console.error(err));
  }

  const reConnectToServer = () => {
    const windowLocationHref = window.location.href;
    const urlPathChunks = windowLocationHref.split("/");
    const urlTokenExtracted = urlPathChunks[urlPathChunks.length-1];

    axios({
      method: "get",
      url: process.env.REACT_APP_BACKEND_URL + `api/matches/get-game-object/${urlTokenExtracted}`,
    })
    .then((res: any) => {
        if (res?.data?.game)
        setGame(res.data.game);
        mapUserDataHelper(res.data.game);      
    })
    .catch((err: any) => console.error(err));
  }

  const mapUserDataHelper = (gameObject: IGame) => {
    const currentPlayerEmailFromLocalStorage: string = localStorage.getItem("userEmailInCaseHeLogsOut") || ""
    const currentPlayer = gameObject?.players?.find((player: any) => { return player.email === currentPlayerEmailFromLocalStorage});
    setUserDataHelper({
			color: currentPlayer?.color || "",
			fullName: currentPlayer?.fullName || "",
			nickname: currentPlayer?.nickname,
			email: currentPlayerEmailFromLocalStorage,
			image: currentPlayer?.image || "",
			isReady: true,
		})
  }

  const setGameState = (data: IGame) => {
    if(!isGamePhaseIsNine) setGame(data);
  }

  const providerValue = {
    teamNumber,
    game,
    players,
    enterTheTeam,
    joinLobby,
    setGameState,
    roomUsers,
    sendMessage,
    setActivateAction,
    activateAction,
    isTheButtonClickedToJoinTheGame,
    numberOfPlayers,  
    setIsTheButtonClickedToJoinTheGame,
    allTeamNames, 
    setAllTeamNames,
    urlToken, 
    setUrlToken,
    gameId, 
    setGameId,
    isLoaderHidden, 
    setisLoaderHidden,
    gameCount, 
    setGameCount,
    getGameLanguage,
    reConnectToServer,
    userDataHelper,
    selectedLanguage,
    setSelectedLanguage
  };

  return (
    <TeamContext.Provider value={providerValue}>
      {props.children}
    </TeamContext.Provider>
  );
};

export { TeamContext, TeamContextProvider };
